import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  MenuItem,
  Paper,
  Slider,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  AutoGraph,
  Bolt,
  CheckCircle,
  CloudQueue,
  EnergySavingsLeaf,
  Insights,
  Settings,
  Speed,
  Thermostat,
  TrendingDown,
  WarningAmber,
  WbSunny,
} from "@mui/icons-material";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import CardTitle from "components/CardTitle";
// import MDButton from "components/MDButton";

const nf = new Intl.NumberFormat("es-ES", { maximumFractionDigits: 2 });
const nf0 = new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0 });

const cardSx = {
  borderRadius: 4,
  boxShadow: "0 18px 50px rgba(15, 23, 42, 0.08)",
  border: "1px solid rgba(148, 163, 184, 0.22)",
};

const softPanelSx = {
  borderRadius: 3,
  p: 2,
  background: "linear-gradient(135deg, rgba(248,250,252,0.98), rgba(241,245,249,0.8))",
  border: "1px solid rgba(148, 163, 184, 0.2)",
};

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getMetricAvatarSx(color) {
  if (color === "default") {
    return { bgcolor: "grey.200", color: "grey.800", width: 40, height: 40 };
  }

  return { bgcolor: `${color}.light`, color: `${color}.dark`, width: 40, height: 40 };
}

function getMetricChipColor(color) {
  if (color === "default") {
    return undefined;
  }

  return color;
}

function MetricCard({ icon, label, value, helper, chip, color = "primary" }) {
  const avatarSx = getMetricAvatarSx(color);

  return (
    <Paper elevation={0} sx={softPanelSx}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar sx={avatarSx}>{icon}</Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="h5" fontWeight={800} color="text.primary">
              {value}
            </Typography>
            {chip && <Chip size="small" color={getMetricChipColor(color)} label={chip} />}
          </Stack>
          {helper && (
            <Typography variant="caption" color="text.secondary">
              {helper}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}

MetricCard.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  helper: PropTypes.string,
  chip: PropTypes.string,
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "success",
    "warning",
    "error",
    "info",
    "default",
  ]),
};

MetricCard.defaultProps = {
  helper: "",
  chip: "",
  color: "primary",
};

function ControlSlider({ label, value, min, max, step, unit, onChange, helper, slider = true }) {
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
        <Box>
          <Typography variant="body2" fontWeight={700}>
            {label}
          </Typography>
          {helper && (
            <Typography variant="caption" color="text.secondary">
              {helper}
            </Typography>
          )}
        </Box>
        <Typography variant="body2" fontWeight={800} color="primary.main">
          {nf.format(value)}
          {unit}
        </Typography>
      </Stack>
      {slider && (
        <Slider
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(_, newValue) => onChange(Number(newValue))}
          valueLabelDisplay="auto"
          sx={{ mt: 1 }}
        />
      )}
    </Box>
  );
}

ControlSlider.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  step: PropTypes.number,
  unit: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  helper: PropTypes.string,
  slider: PropTypes.bool,
};

ControlSlider.defaultProps = {
  step: 1,
  unit: "",
  helper: "",
  slider: true,
};

const historical7d = [
  { day: "L", consumo: 1420, temp: 16.1 },
  { day: "M", consumo: 1510, temp: 15.3 },
  { day: "X", consumo: 1475, temp: 15.8 },
  { day: "J", consumo: 1585, temp: 14.9 },
  { day: "V", consumo: 1660, temp: 14.2 },
  { day: "S", consumo: 1240, temp: 17.6 },
  { day: "D", consumo: 1125, temp: 18.2 },
];

const forecastBase = [
  { day: "D+1", temp: 21.2, radiacion: 74 },
  { day: "D+2", temp: 20.4, radiacion: 68 },
  { day: "D+3", temp: 18.7, radiacion: 52 },
  { day: "D+4", temp: 17.9, radiacion: 44 },
  { day: "D+5", temp: 19.3, radiacion: 59 },
  { day: "D+6", temp: 22.1, radiacion: 81 },
  { day: "D+7", temp: 23.0, radiacion: 85 },
];

const benchmarkRows = [
  { tipo: "Vivienda colectiva", zona: "C1", benchmark: 20 },
  { tipo: "Vivienda unifamiliar", zona: "C1", benchmark: 25 },
  { tipo: "Oficina", zona: "C1", benchmark: 35 },
  { tipo: "Hospital", zona: "C1", benchmark: 45 },
];

function getRenewableStateColor(state) {
  if (state === "PV_BOOST") return "success";
  if (state === "PV_PREP") return "warning";
  return "default";
}

function getLeedStatus(leed) {
  if (leed >= 20) return "Buen desempeño";
  if (leed >= 10) return "Mejora moderada";
  return "Requiere optimización";
}

function getLeedColor(leed) {
  if (leed >= 20) return "success";
  if (leed >= 10) return "warning";
  return "error";
}

function getHeroButtonSx(isActive) {
  return {
    color: isActive ? "#0f172a" : "#64748b",
    bgcolor: isActive ? "white" : "rgba(226, 226, 226, 1)",
    borderColor: "rgba(255,255,255,0.5)",
  };
}

function EfficiencyIndicatorsModule() {
  const [buildingType, setBuildingType] = useState("Oficina");
  const [surface, setSurface] = useState(7200);
  const [baselineCO2, setBaselineCO2] = useState(330000);
  const [electricity, setElectricity] = useState(410750);
  const [gas, setGas] = useState(0);
  const [diesel, setDiesel] = useState(0);
  const [biomass, setBiomass] = useState(68000);

  const result = useMemo(() => {
    const rows = [
      { fuente: "Electricidad red", factor: 0.231, consumo: electricity },
      { fuente: "Gas natural", factor: 0.204, consumo: gas },
      { fuente: "Gasoil C", factor: 0.267, consumo: diesel },
      { fuente: "Biomasa", factor: 0.03, consumo: biomass },
    ].map((row) => ({ ...row, emisiones: row.consumo * row.factor }));

    const totalCO2 = rows.reduce((acc, row) => acc + row.emisiones, 0);
    const breeam = totalCO2 / surface;
    const leed = ((baselineCO2 - totalCO2) / baselineCO2) * 100;
    const benchmark = benchmarkRows.find((row) => row.tipo === buildingType)?.benchmark ?? 35;
    const benchmarkStatus =
      breeam <= benchmark ? "Mejor que benchmark" : "Por encima del benchmark";
    const leedStatus = getLeedStatus(leed);

    return { rows, totalCO2, breeam, leed, benchmark, benchmarkStatus, leedStatus };
  }, [baselineCO2, biomass, buildingType, diesel, electricity, gas, surface]);

  return (
    <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
      <Stack spacing={3}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: "secondary.light", color: "secondary.dark" }}>
              <Speed />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={900}>
                Indicadores de eficiencia en tiempo real
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cálculo de emisiones, indicador frente a baseline ASHRAE 90.1.
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              color={result.breeam <= result.benchmark ? "success" : "warning"}
              label={result.benchmarkStatus}
            />
            <Chip color={getLeedColor(result.leed)} label={result.leedStatus} />
          </Stack>
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
            gap: 2,
          }}
        >
          <MetricCard
            icon={<EnergySavingsLeaf />}
            label="Emisiones anuales"
            value={`${nf0.format(result.totalCO2)} kg CO₂e`}
            helper="Σ Ci · fi"
            color="secondary"
          />
          <MetricCard
            icon={<Building2Icon />}
            label="Indicador BREEAM"
            value={`${nf.format(result.breeam)} kg/m²·año`}
            helper={`Benchmark ${buildingType} C1: ${result.benchmark}`}
            color={result.breeam <= result.benchmark ? "success" : "warning"}
          />
          <MetricCard
            icon={<TrendingDown />}
            label="Indicador LEED v4.1"
            value={`${nf.format(result.leed)} %`}
            helper="Reducción frente al baseline"
            color={result.leed >= 20 ? "success" : "warning"}
          />
          <MetricCard
            icon={<Insights />}
            label="Superficie útil"
            value={`${nf0.format(surface)} m²`}
            helper="Normalización de emisiones"
            color="primary"
          />
        </Box>

        <Box
          sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "0.9fr 1.1fr" }, gap: 2 }}
        >
          <Paper elevation={0} sx={softPanelSx}>
            <Stack spacing={2.2}>
              <Typography variant="subtitle1" fontWeight={800}>
                Configuración de indicadores
              </Typography>
              <TextField
                select
                fullWidth
                label="Tipo de edificio"
                value={buildingType}
                onChange={(event) => setBuildingType(event.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    minHeight: 56,
                    borderRadius: "8px",
                  },

                  "& .MuiSelect-select": {
                    paddingTop: "16px",
                    paddingBottom: "16px",
                    display: "flex",
                    alignItems: "center",
                  },
                }}
              >
                {benchmarkRows.map((row) => (
                  <MenuItem key={row.tipo} value={row.tipo}>
                    {row.tipo} · zona {row.zona} · {row.benchmark} kg CO₂e/m²·año
                  </MenuItem>
                ))}
              </TextField>
              <ControlSlider
                label="Superficie útil"
                value={surface}
                min={1000}
                max={25000}
                step={100}
                unit=" m²"
                onChange={setSurface}
                helper="Área utilizada para normalizar BREEAM"
              />
              <ControlSlider
                label="Baseline CO₂ ASHRAE"
                value={baselineCO2}
                min={120000}
                max={650000}
                step={5000}
                unit=" kg"
                onChange={setBaselineCO2}
                helper="Edificio de referencia para LEED"
              />
              <Divider />
              <ControlSlider
                label="Electricidad red"
                value={electricity}
                min={100000}
                max={800000}
                step={5000}
                slider={false}
                unit=" kWh/año"
                onChange={setElectricity}
                helper="fi = 0,231 kg CO₂e/kWh"
              />
              <ControlSlider
                label="Gas natural"
                value={gas}
                min={0}
                max={400000}
                step={5000}
                slider={false}
                unit=" kWh/año"
                onChange={setGas}
                helper="fi = 0,204 kg CO₂e/kWh"
              />
              <ControlSlider
                label="Gasoil C"
                value={diesel}
                min={0}
                max={120000}
                step={2000}
                unit=" kWh/año"
                onChange={setDiesel}
                helper="fi = 0,267 kg CO₂e/kWh"
              />
              <ControlSlider
                label="Biomasa"
                value={biomass}
                min={0}
                max={160000}
                step={2000}
                unit=" kWh/año"
                onChange={setBiomass}
                helper="fi = 0,030 kg CO₂e/kWh"
              />
            </Stack>
          </Paper>

          <Paper elevation={0} sx={softPanelSx}>
            <Stack spacing={2}>
              <Typography variant="subtitle1" fontWeight={800}>
                Emisiones por fuente energética
              </Typography>
              <Box sx={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={result.rows} margin={{ left: 0, right: 12, top: 12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="fuente" tick={{ fontSize: 14 }} />

                    <YAxis
                      width={80}
                      tick={{ fontSize: 14 }}
                      tickFormatter={(value) => nf0.format(value)}
                    />

                    <RechartsTooltip
                      formatter={(value, name) => [
                        name === "emisiones"
                          ? `${nf0.format(value)} kg CO₂e`
                          : `${nf0.format(value)} kWh`,
                        name === "emisiones" ? "Emisiones" : "Consumo",
                      ]}
                    />

                    <Bar dataKey="emisiones" fill="#7c3aed" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>

              <Table
                size="small"
                sx={{
                  width: "100%",
                  tableLayout: "fixed",
                  "& .MuiTableHead-root": {
                    display: "table-header-group",
                  },
                  "& .MuiTableBody-root": {
                    display: "table-row-group",
                  },
                  "& .MuiTableCell-root": {
                    verticalAlign: "middle",
                    py: 1.2,
                    px: 1.5,
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <colgroup>
                  <col style={{ width: "34%" }} />
                  <col style={{ width: "26%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "26%" }} />
                </colgroup>

                <TableHead>
                  <TableRow>
                    <TableCell>Fuente</TableCell>
                    <TableCell align="right">Consumo</TableCell>
                    <TableCell align="right">fi</TableCell>
                    <TableCell align="right">Emisiones</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {result.rows.map((row) => (
                    <TableRow key={row.fuente} hover>
                      <TableCell>{row.fuente}</TableCell>
                      <TableCell align="right">{nf0.format(row.consumo)} kWh</TableCell>
                      <TableCell align="right">{row.factor.toFixed(3)}</TableCell>
                      <TableCell align="right">{nf0.format(row.emisiones)} kg</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Alert severity="success" sx={{ borderRadius: 3 }}>
                BREEAM mide impacto absoluto por m²; LEED mide mejora relativa frente al baseline.
                Ambos indicadores se recalculan en tiempo real al modificar consumos, superficie o
                baseline.
              </Alert>
            </Stack>
          </Paper>
        </Box>
      </Stack>
    </CardContent>
  );
}

function PredictionModule() {
  const [gamma, setGamma] = useState(0.035);
  const [alpha, setAlpha] = useState(0.62);
  const [tempTomorrow, setTempTomorrow] = useState(11);
  const [monthHistorical, setMonthHistorical] = useState(48500);
  const [monthForecastTemp, setMonthForecastTemp] = useState(12.5);

  const result = useMemo(() => {
    const consumo7d = historical7d.reduce((acc, item) => acc + item.consumo, 0);
    const avgTemp7d = historical7d.reduce((acc, item) => acc + item.temp, 0) / historical7d.length;
    const deltaT7d = tempTomorrow - avgTemp7d;
    const adjusted7d = consumo7d * (1 + gamma * deltaT7d);

    const historicalMonthTemp = 16.7;
    const deltaTMonth = monthForecastTemp - historicalMonthTemp;
    const adjustedMonth = monthHistorical * (1 + gamma * deltaTMonth);
    const beta = 1 - alpha;
    const estimatedDay = alpha * (adjusted7d / 7) + beta * (adjustedMonth / 30);

    const realToday = 1640;
    const deviation = ((realToday - estimatedDay) / estimatedDay) * 100;
    const anomaly = Math.abs(deviation) > 12;

    const projection = forecastBase.map((item, index) => {
      const delta = item.temp - avgTemp7d;
      const workdayFactor = index < 5 ? 1 : 0.78;
      const prediction = (adjusted7d / 7) * (1 + gamma * delta) * workdayFactor;
      return {
        ...item,
        previsto: Math.round(prediction),
        real: index === 0 ? realToday : null,
      };
    });

    return {
      consumo7d,
      avgTemp7d,
      deltaT7d,
      adjusted7d,
      adjustedMonth,
      estimatedDay,
      deviation,
      anomaly,
      beta,
      projection,
    };
  }, [alpha, gamma, monthForecastTemp, monthHistorical, tempTomorrow]);

  return (
    <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
      <Stack spacing={3}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: "primary.light", color: "primary.dark" }}>
              <AutoGraph />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={900}>
                Predicción de consumos futuros
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Estimación diaria y mensual con histórico, meteorología, pesos adaptativos y
                detección de anomalías.
              </Typography>
            </Box>
          </Stack>
          <Chip
            color={result.anomaly ? "warning" : "success"}
            icon={result.anomaly ? <WarningAmber /> : <CheckCircle />}
            label={result.anomaly ? "Desviación relevante" : "Operación estable"}
          />
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
            gap: 2,
          }}
        >
          <MetricCard
            icon={<Bolt />}
            label="Consumo estimado mañana"
            value={`${nf0.format(result.estimatedDay)} kWh`}
            helper="Combinación ponderada 7d + mes"
            chip={`α ${nf.format(alpha)}`}
            color="primary"
          />
          <MetricCard
            icon={<CloudQueue />}
            label="Temperatura prevista"
            value={`${nf.format(tempTomorrow)} ºC`}
            helper={`ΔT 7d: ${nf.format(result.deltaT7d)} ºC`}
            color="info"
          />
          <MetricCard
            icon={<Insights />}
            label="Consumo ajustado 7d"
            value={`${nf0.format(result.adjusted7d)} kWh`}
            helper={`Histórico 7d: ${nf0.format(result.consumo7d)} kWh`}
            color="secondary"
          />
          <MetricCard
            icon={<WarningAmber />}
            label="Desviación real vs previsto"
            value={`${nf.format(result.deviation)} %`}
            helper="Umbral de alerta: ±12 %"
            color={result.anomaly ? "warning" : "success"}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1.15fr 0.85fr" },
            gap: 2,
          }}
        >
          <Paper elevation={0} sx={softPanelSx}>
            <Stack spacing={1}>
              <Typography variant="subtitle1" fontWeight={800}>
                Predicción de consumo a 7 días
              </Typography>
              <Box sx={{ height: 290 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={result.projection}
                    margin={{ left: -18, right: 12, top: 12, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" tick={{ fontSize: 14 }} />
                    <YAxis
                      width={80}
                      tick={{ fontSize: 14 }}
                      tickFormatter={(value) => nf0.format(value)}
                    />
                    <RechartsTooltip
                      formatter={(value, name) => [
                        `${nf0.format(value)} kWh`,
                        name === "previsto" ? "Consumo previsto" : "Consumo real",
                      ]}
                    />
                    <Area
                      type="monotone"
                      dataKey="previsto"
                      stroke="#2563eb"
                      fill="#bfdbfe"
                      strokeWidth={3}
                    />
                    <Line
                      type="monotone"
                      dataKey="real"
                      stroke="#f97316"
                      strokeWidth={3}
                      dot={{ r: 5 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </Stack>
          </Paper>

          <Paper elevation={0} sx={softPanelSx}>
            <Stack spacing={2.2}>
              <Typography variant="subtitle1" fontWeight={800}>
                Parámetros del modelo
              </Typography>
              <ControlSlider
                label="Sensibilidad γ"
                value={gamma}
                min={0}
                max={0.08}
                step={0.005}
                slider={false}
                onChange={setGamma}
                helper="Variación del consumo por cada ºC"
              />
              <ControlSlider
                label="Peso α histórico 7d"
                value={alpha}
                min={0.1}
                max={0.9}
                step={0.05}
                slider={false}
                onChange={setAlpha}
                helper={`β mensual = ${nf.format(result.beta)}`}
              />
              <ControlSlider
                label="Temperatura mañana"
                value={tempTomorrow}
                min={0}
                max={40}
                step={0.2}
                slider={false}
                unit=" ºC"
                onChange={setTempTomorrow}
                helper="Dato OpenWeather"
              />
              <ControlSlider
                label="Histórico del mismo mes"
                value={monthHistorical}
                min={35000}
                max={65000}
                step={500}
                slider={false}
                unit=" kWh"
                onChange={setMonthHistorical}
                helper="Consumo mensual histórico"
              />
              <ControlSlider
                label="Temperatura prevista mes"
                value={monthForecastTemp}
                min={0}
                max={40}
                step={0.2}
                slider={false}
                unit=" ºC"
                onChange={setMonthForecastTemp}
                helper="Media prevista próximos días"
              />
            </Stack>
          </Paper>
        </Box>

        <Alert severity="info" sx={{ borderRadius: 3 }}>
          Fórmula: Consumo estimado día = α · consumo ajustado 7d / 7 + β · consumo ajustado mes /
          días del mes. La comparación con el consumo real activa la alerta de anomalía.
        </Alert>
      </Stack>
    </CardContent>
  );
}

function RenewableManagerModule() {
  const [pvGeneration, setPvGeneration] = useState(118);
  const [buildingDemand, setBuildingDemand] = useState(82);
  const [zoneDemand, setZoneDemand] = useState(8.5);
  const [comfortBand, setComfortBand] = useState(1.5);
  const [holdEnabled, setHoldEnabled] = useState(true);

  function getRenewableState(surplus, modifiedZones) {
    if (surplus <= 4) return "NORMAL";
    if (modifiedZones < 3) return "PV_PREP";
    return "PV_BOOST";
  }

  const result = useMemo(() => {
    const surplus = pvGeneration - buildingDemand;
    const maxZones = surplus > 0 ? Math.floor(surplus / zoneDemand) : 0;
    const modifiedZones = clamp(maxZones, 0, 8);
    const thermalStorage = modifiedZones * zoneDemand * 0.25;
    const selfConsumption = clamp(
      (buildingDemand + modifiedZones * zoneDemand) / pvGeneration,
      0,
      1
    );
    const avoidedExport = Math.max(0, modifiedZones * zoneDemand);
    const state = getRenewableState(surplus, modifiedZones);

    const nextCheck =
      state === "NORMAL"
        ? "Transición a PV_BOOST si excedente > 15 min"
        : "HOLD: comprobar cada 15 min";

    const zoneProfiles = [
      { name: "Norte P1", factor: 0.92 },
      { name: "Sur P1", factor: 1.18 },
      { name: "Este P1", factor: 0.84 },
      { name: "Oeste P1", factor: 1.12 },
      { name: "Norte PB", factor: 0.68 },
      { name: "Sur PB", factor: 1.36 },
      { name: "Este PB", factor: 1.05 },
      { name: "Oeste PB", factor: 0.77 },
    ];

    const zones = zoneProfiles.map((zone, index) => ({
      name: zone.name,
      demand: Number((zoneDemand * zone.factor).toFixed(1)),
      setpoint: index < modifiedZones ? 20 + comfortBand * 0.6 : 20,
      active: index < modifiedZones,
    }));

    return {
      surplus,
      maxZones,
      modifiedZones,
      thermalStorage,
      selfConsumption,
      avoidedExport,
      state,
      nextCheck,
      zones,
    };
  }, [buildingDemand, comfortBand, pvGeneration, zoneDemand]);

  const stateColor = getRenewableStateColor(result.state);

  return (
    <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
      <Stack spacing={3}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: "success.light", color: "success.dark" }}>
              <EnergySavingsLeaf />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={900}>
                Gestor de energías renovables
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Máquina de estados para priorizar autoconsumo, evitar vertido y almacenar excedentes
                como energía térmica.
              </Typography>
            </Box>
          </Stack>
          <Chip color={stateColor} icon={<Settings />} label={`Estado: ${result.state}`} />
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
            gap: 2,
          }}
        >
          <MetricCard
            icon={<WbSunny />}
            label="Generación renovable"
            value={`${nf.format(pvGeneration)} kW`}
            helper="Fotovoltaica / renovable local"
            color="warning"
          />
          <MetricCard
            icon={<Bolt />}
            label="Demanda edificio"
            value={`${nf.format(buildingDemand)} kW`}
            helper="Demanda instantánea"
            color="primary"
          />
          <MetricCard
            icon={<EnergySavingsLeaf />}
            label="Excedente disponible"
            value={`${nf.format(result.surplus)} kW`}
            helper="Generación - demanda"
            chip={result.surplus > 0 ? "aprovechable" : "sin excedente"}
            color={result.surplus > 0 ? "success" : "default"}
          />
          <MetricCard
            icon={<Thermostat />}
            label="Zonas modificables"
            value={`${result.modifiedZones} / 8`}
            helper={`${nf.format(result.avoidedExport)} kW no vertidos`}
            color="success"
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "0.85fr 1.15fr" },
            gap: 2,
          }}
        >
          <Paper elevation={0} sx={softPanelSx}>
            <Stack spacing={2.2}>
              <Typography variant="subtitle1" fontWeight={800}>
                Datos de entrada
              </Typography>
              <ControlSlider
                label="Generación renovable"
                value={pvGeneration}
                min={10}
                max={160}
                step={1}
                unit=" kW"
                onChange={setPvGeneration}
                helper="Producción renovable instantánea"
              />
              <ControlSlider
                label="Demanda actual"
                value={buildingDemand}
                min={25}
                max={140}
                step={1}
                unit=" kW"
                onChange={setBuildingDemand}
                helper="Consumo eléctrico del edificio"
              />
              <ControlSlider
                label="Demanda por zona"
                value={zoneDemand}
                min={3}
                max={16}
                step={0.5}
                unit=" kW"
                onChange={setZoneDemand}
                helper="Potencia estimada de climatización"
              />
              <ControlSlider
                label="Banda máxima de confort"
                value={comfortBand}
                min={0.5}
                max={3}
                step={0.1}
                unit=" ºC"
                onChange={setComfortBand}
                helper="Límite configurable por cliente"
              />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" fontWeight={700}>
                    Ciclo HOLD activo
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Revisión cíclica cada 15 minutos
                  </Typography>
                </Box>
                <Switch
                  checked={holdEnabled}
                  onChange={(event) => setHoldEnabled(event.target.checked)}
                />
              </Stack>
            </Stack>
          </Paper>

          <Paper elevation={0} sx={softPanelSx}>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                spacing={1}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={800}>
                    Estado operativo y zonas
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {holdEnabled ? result.nextCheck : "HOLD desactivado para simulación manual"}
                  </Typography>
                </Box>
                <Chip
                  color="success"
                  label={`Autoconsumo ${nf.format(result.selfConsumption * 100)} %`}
                />
              </Stack>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                  gap: 1.5,
                }}
              >
                <Paper elevation={0} sx={{ ...softPanelSx, bgcolor: "rgba(240,253,244,0.82)" }}>
                  <Typography variant="caption" color="text.secondary">
                    NORMAL → PV_BOOST
                  </Typography>
                  <Typography variant="h6" fontWeight={900}>
                    15 min
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Espera antes de activar excedentes
                  </Typography>
                </Paper>
                <Paper elevation={0} sx={{ ...softPanelSx, bgcolor: "rgba(255,251,235,0.82)" }}>
                  <Typography variant="caption" color="text.secondary">
                    HOLD
                  </Typography>
                  <Typography variant="h6" fontWeight={900}>
                    15 min
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Comprobación periódica
                  </Typography>
                </Paper>
                <Paper elevation={0} sx={{ ...softPanelSx, bgcolor: "rgba(239,246,255,0.82)" }}>
                  <Typography variant="caption" color="text.secondary">
                    Almacenamiento térmico
                  </Typography>
                  <Typography variant="h6" fontWeight={900}>
                    {nf.format(result.thermalStorage)} kWh
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Estimado en ciclo de 15 min
                  </Typography>
                </Paper>
              </Box>

              <Box sx={{ height: 270 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={result.zones} margin={{ left: 0, right: 10, top: 12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 14 }} />
                    <YAxis
                      width={50}
                      tick={{ fontSize: 14 }}
                      tickFormatter={(value) => nf0.format(value)}
                    />
                    <RechartsTooltip
                      formatter={(value, name) => [
                        name === "demand" ? `${nf.format(value)} kW` : `${nf.format(value)} ºC`,
                        name === "demand" ? "Demanda" : "Consigna",
                      ]}
                    />
                    <Bar dataKey="demand" radius={[8, 8, 0, 0]}>
                      {result.zones.map((zone) => (
                        <Cell key={zone.name} fill={zone.active ? "#16a34a" : "#94a3b8"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>

              <Table
                size="small"
                sx={{
                  width: "100%",
                  tableLayout: "fixed",
                  "& .MuiTableHead-root": {
                    display: "table-header-group",
                  },
                  "& .MuiTableBody-root": {
                    display: "table-row-group",
                  },
                  "& .MuiTableCell-root": {
                    verticalAlign: "middle",
                    py: 1.2,
                    px: 1.5,
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <colgroup>
                  <col style={{ width: "25%" }} />
                  <col style={{ width: "25%" }} />
                  <col style={{ width: "25%" }} />
                  <col style={{ width: "25%" }} />
                </colgroup>

                <TableHead>
                  <TableRow>
                    <TableCell>Zona</TableCell>
                    <TableCell align="right">Demanda</TableCell>
                    <TableCell align="right">Consigna</TableCell>
                    <TableCell align="right">Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.zones.map((zone) => (
                    <TableRow key={zone.name} hover>
                      <TableCell>{zone.name}</TableCell>
                      <TableCell align="right">{nf.format(zone.demand)} kW</TableCell>
                      <TableCell align="right">{nf.format(zone.setpoint)} ºC</TableCell>
                      <TableCell align="right">
                        <Chip
                          size="small"
                          color={zone.active ? "success" : "default"}
                          label={zone.active ? "Modificar" : "Mantener"}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Stack>
          </Paper>
        </Box>
      </Stack>
    </CardContent>
  );
}

function Building2Icon() {
  return <Insights />;
}

export default function DashboardCertifications() {
  const [activeModule, setActiveModule] = useState("indicadores");

  const modules = [
    { id: "indicadores", label: "Indicadores", icon: <Speed fontSize="small" /> },
    { id: "prediccion", label: "Predicción", icon: <AutoGraph fontSize="small" /> },
    { id: "renovables", label: "Renovables", icon: <EnergySavingsLeaf fontSize="small" /> },
  ];

  return (
    <Box sx={{ minHeight: "100vh", maxWidth: 1480, mx: "auto" }}>
      <Card elevation={0} sx={cardSx}>
        <Stack spacing={3} sx={{ position: "relative" }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            spacing={2}
            alignItems={{ xs: "flex-start", md: "center" }}
          >
            <CardTitle title="CERTIFICACIONES" bgColor="success" />

            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              justifyContent={{ xs: "flex-start", md: "flex-end" }}
              sx={{
                pt: 3,
                px: 2,
                // backgroundColor: "rgba(226, 226, 226, 1)",
              }}
            >
              {modules.map((module) => (
                <Button
                  key={module.id}
                  startIcon={module.icon}
                  variant={activeModule === module.id ? "contained" : "outlined"}
                  color="inherit"
                  onClick={() => setActiveModule(module.id)}
                  sx={getHeroButtonSx(activeModule === module.id)}
                >
                  {module.label}
                </Button>
              ))}
            </Stack>
          </Stack>
        </Stack>
        <Stack spacing={3}>
          {activeModule === "indicadores" && <EfficiencyIndicatorsModule />}
          {activeModule === "prediccion" && <PredictionModule />}
          {activeModule === "renovables" && <RenewableManagerModule />}
        </Stack>
      </Card>
    </Box>
  );
}
